import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SECTION_META, sectionKeys, buildRecommendation } from "./case-analysis/reviewMeta";
import { CaseIntake } from "./case-analysis/CaseIntake";
import { CaseProcessing } from "./case-analysis/CaseProcessing";
import { ReviewChecklist } from "./case-analysis/ReviewChecklist";
import { ReviewCommandCard } from "./case-analysis/ReviewCommandCard";
import { ReviewSectionContainer } from "./case-analysis/ReviewSectionContainer";
import { ReviewSectionBody } from "./case-analysis/ReviewSectionBody";
import { ReviewOverview } from "./case-analysis/ReviewOverview";
import { EstimateGovernance } from "./case-analysis/EstimateGovernance";
import { useCurrencyContext } from "@/lib/currency";
import { ApprovalGuardrailDialog } from "./case-analysis/ApprovalGuardrailDialog";

const CONF_TONE = { High: "bg-emerald-50 text-emerald-700 border-emerald-200", Medium: "bg-amber-50 text-amber-700 border-amber-200", Low: "bg-slate-100 text-slate-500 border-slate-200" };

/**
 * Merge enriched pricing fields onto stored recommended_services for pre-fix analyses.
 * Called when the pricing-context endpoint returns on-demand enrichment.
 * Preserves any price the staff manually edited (price != library_price).
 */
function mergeEnrichedPricing(storedServices, enrichedServices) {
  const enrichedById = Object.fromEntries(
    enrichedServices.filter(s => s.catalog_id).map(s => [s.catalog_id, s])
  );
  return storedServices.map(s => {
    const enriched = enrichedById[s.catalog_id];
    if (!enriched?.pricing_evidence) return s;
    // If staff hasn't customized the price (still at library default or unset), apply evidence price
    const libraryPrice = enriched.library_price ?? s.price;
    const priceAtDefault = s.price == null || Math.abs((s.price || 0) - (libraryPrice || 0)) < 0.01;
    return {
      ...s,
      library_price: enriched.library_price,
      pricing_source: enriched.pricing_source,
      pricing_evidence: enriched.pricing_evidence,
      price: priceAtDefault ? enriched.price : s.price,
    };
  });
}

// Staff-only Knowledge Retrieval Summary / Research Package — collapsible. Never client/Penny-facing.
function KnowledgeRetrievalPanel({ research, summary }) {
  const [open, setOpen] = useState(false);
  if (!research && !summary) return null;
  const r = research || {};
  const s = summary || {};
  const conf = r.confidence || s.match_confidence || "Medium";
  const authorities = r.authorities || [];
  const related = r.related_authorities || [];
  const gaps = r.knowledge_gaps || [];
  const topics = r.topics || s.topics || [];
  const jurisdictions = r.jurisdictions || (s.jurisdiction ? [s.jurisdiction] : []);
  return (
    <div className="mb-4 rounded-xl border border-indigo-100 bg-indigo-50/40 overflow-hidden" data-testid="ca-knowledge-retrieval">
      <button onClick={() => setOpen((o) => !o)} data-testid="ca-knowledge-toggle"
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-indigo-50 transition-colors">
        <Icons.Sparkles className="h-4 w-4 text-indigo-500 shrink-0" />
        <span className="text-sm font-semibold text-indigo-800 flex-1">Knowledge Retrieval Summary
          <span className="ml-2 text-[11px] font-normal text-indigo-500">· {authorities.length} authorities · staff-only</span>
        </span>
        <span className={`text-[11px] px-2 py-0.5 rounded-full border font-medium ${CONF_TONE[conf] || CONF_TONE.Medium}`}>{conf} confidence</span>
        <Icons.ChevronDown className={`h-4 w-4 text-indigo-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-[13px]" data-testid="ca-knowledge-body">
          {jurisdictions.length > 0 && <div><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Jurisdictions detected</div><div className="flex flex-wrap gap-1.5">{jurisdictions.map((j, i) => <span key={i} className="text-xs bg-blue-50 text-blue-700 rounded px-1.5 py-0.5">{j}</span>)}</div></div>}
          {topics.length > 0 && <div><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Topics extracted</div><div className="flex flex-wrap gap-1.5">{topics.slice(0, 10).map((t, i) => <span key={i} className="text-xs bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{t}</span>)}</div></div>}
          <div className="md:col-span-2"><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Authorities consulted</div>
            {authorities.length === 0 ? <div className="text-slate-400 text-xs">None matched.</div> :
              <ul className="space-y-1">{authorities.map((a, i) => (
                <li key={i} className="flex items-center gap-2"><Icons.FileText className="h-3.5 w-3.5 text-indigo-400 shrink-0" /><span className="text-slate-700 truncate">{a.title}</span>
                  {a.authority_level && <span className="text-[10px] bg-indigo-50 text-indigo-600 rounded px-1 shrink-0">{a.authority_level}</span>}
                  {(a.matched_on || []).length > 0 && <span className="text-[10px] text-slate-400 shrink-0">matched: {a.matched_on.join(", ")}</span>}
                </li>))}</ul>}
          </div>
          {related.length > 0 && <div className="md:col-span-2"><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Related authorities</div>
            <ul className="space-y-0.5">{related.map((rl, i) => <li key={i} className="text-slate-500 text-xs">• {rl.title} <span className="text-slate-400">({(rl.relationship_type || "").replace(/_/g, " ")})</span></li>)}</ul></div>}
          {gaps.length > 0 && <div className="md:col-span-2"><div className="text-[11px] font-semibold uppercase tracking-wide text-amber-500 mb-1">Knowledge gaps</div>
            <ul className="space-y-0.5">{gaps.map((g, i) => <li key={i} className="text-amber-700 text-xs flex items-start gap-1"><Icons.AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />{g}</li>)}</ul></div>}
          <div className="md:col-span-2 flex items-center justify-between text-[11px] text-slate-400 pt-1 border-t border-indigo-100">
            <span>Retrieval: {s.retrieval_method || r.metrics?.retrieval_method || "—"}{r.metrics?.retrieval_ms != null ? ` · ${Math.round(r.metrics.retrieval_ms)}ms` : ""}</span>
            <span className="italic">The source documents remain the legal authority.</span>
          </div>
        </div>
      )}
    </div>
  );
}

// Staff-only Historical Context (Case Context Package) — collapsible. Never client/Penny-facing.
function HistoricalContextPanel({ context }) {
  const [open, setOpen] = useState(false);
  if (!context || (context.metrics?.records_retrieved || 0) === 0) return null;
  const c = context;
  const facts = c.relevant_historical_facts || [];
  const decisions = c.prior_decisions || [];
  const outstanding = c.outstanding_questions || [];
  const contradictions = c.contradictions || [];
  return (
    <div className="mb-4 rounded-xl border border-amber-100 bg-amber-50/40 overflow-hidden" data-testid="ca-historical-context">
      <button onClick={() => setOpen((o) => !o)} data-testid="ca-history-toggle"
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-amber-50 transition-colors">
        <Icons.History className="h-4 w-4 text-amber-600 shrink-0" />
        <span className="text-sm font-semibold text-amber-800 flex-1">Historical Context
          <span className="ml-2 text-[11px] font-normal text-amber-600">· {c.metrics.records_retrieved} records · staff-only</span>
        </span>
        {contradictions.length > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200 font-medium">{contradictions.length} contradiction{contradictions.length > 1 ? "s" : ""}</span>}
        <Icons.ChevronDown className={`h-4 w-4 text-amber-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 space-y-3 text-[13px]" data-testid="ca-history-body">
          {contradictions.length > 0 && (
            <div><div className="text-[11px] font-semibold uppercase tracking-wide text-rose-500 mb-1">Contradictions (staff to resolve — not auto-resolved)</div>
              <ul className="space-y-1">{contradictions.map((ct, i) => (
                <li key={i} className="text-xs text-rose-700 bg-rose-50 rounded px-2 py-1"><span className="font-semibold">{ct.field}:</span> {ct.values.map((v) => `${v.value} (${v.reliability_label})`).join("  vs  ")}</li>
              ))}</ul></div>
          )}
          {facts.length > 0 && (
            <div><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Relevant historical facts</div>
              <ul className="space-y-1">{facts.slice(0, 12).map((ftc, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-700">
                  <Icons.Dot className="h-4 w-4 text-amber-400 shrink-0" />
                  <span className="flex-1">{ftc.fact}
                    <span className="ml-1 text-[10px] bg-slate-100 text-slate-500 rounded px-1">{ftc.reliability_label}</span>
                    {ftc.support_count > 1 && <span className="ml-1 text-[10px] text-emerald-600">{ftc.support_count} sources</span>}
                    {ftc.superseded && <span className="ml-1 text-[10px] bg-slate-200 text-slate-500 rounded px-1">superseded</span>}
                  </span>
                </li>))}</ul></div>
          )}
          {decisions.length > 0 && (
            <div><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Prior decisions</div>
              <ul className="space-y-0.5">{decisions.slice(0, 8).map((d, i) => <li key={i} className="text-xs text-slate-500">• {d.decision} {d.approved && <span className="text-emerald-600">(approved)</span>}</li>)}</ul></div>
          )}
          {outstanding.length > 0 && (
            <div><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Outstanding questions</div>
              <ul className="space-y-0.5">{outstanding.slice(0, 8).map((q, i) => <li key={i} className="text-xs text-slate-500">• {q.question}</li>)}</ul></div>
          )}
          <div className="flex items-center justify-between text-[11px] text-slate-400 pt-1 border-t border-amber-100">
            <span>Retrieval: {c.metrics.retrieval_ms != null ? `${Math.round(c.metrics.retrieval_ms)}ms` : "—"} · {c.metrics.records_retrieved} records · {c.metrics.contradictions_identified} contradictions</span>
            <span className="italic">Historical assertions — prior AI output is not self-validating.</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CaseAnalysis() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const currencyCtx = useCurrencyContext(clientId);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [confirmApprove, setConfirmApprove] = useState(false);
  const [activeKey, setActiveKey] = useState(null);
  const [navOpen, setNavOpen] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);
  const pollRef = useRef(null);
  const initRef = useRef(false);
  const servicesDirtyRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/case-analysis/${clientId}`);
      setData(d.exists ? d : { exists: false });
    } catch { setData({ exists: false }); }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  // Close the approval guardrail dialog on Escape.
  useEffect(() => {
    if (!confirmApprove) return;
    const onKey = (e) => { if (e.key === "Escape") setConfirmApprove(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirmApprove]);

  // Initialize the active review section once the analysis is ready.
  useEffect(() => {
    if (initRef.current) return;
    if (!data?.exists || !data?.id) return;
    if (["processing", "failed"].includes(data.status)) return;
    initRef.current = true;
    const keys = sectionKeys(data);
    let stored = null;
    try { stored = localStorage.getItem(`ca-active-${clientId}`); } catch { /* noop */ }
    const first = keys.includes(stored) ? stored : keys[0];
    setActiveKey(first);
    const cur = (data.section_reviews || {})[first];
    if (!cur || cur === "not_reviewed") {
      const next = { ...(data.section_reviews || {}), [first]: "in_review" };
      setData((d) => ({ ...d, section_reviews: next }));
      api.patch(`/case-analysis/${data.id}`, { section_reviews: next }).catch(() => { /* noop */ });
    }
  }, [data, clientId]);

  // poll while processing
  useEffect(() => {
    if (data?.status === "processing") {
      pollRef.current = setInterval(load, 4000);
      return () => clearInterval(pollRef.current);
    }
  }, [data?.status, load]);

  // Lazy pricing-context enrichment for pre-fix analyses (backward compatibility).
  // When any catalog-linked service is missing pricing_evidence, fetch enriched context
  // from the backend (deterministic, read-only, no MongoDB write) and merge into local state.
  useEffect(() => {
    if (!data?.id || !data?.recommended_services?.length) return;
    const needsEnrichment = data.recommended_services.some(
      s => s.catalog_id && !s.pricing_evidence
    );
    if (!needsEnrichment) return;
    api.get(`/case-analysis/${data.id}/pricing-context`)
      .then(({ data: ctx }) => {
        if (!ctx?.recommended_services?.length || !ctx.enriched_on_demand) return;
        setData(prev => ({
          ...prev,
          recommended_services: mergeEnrichedPricing(
            prev.recommended_services || [],
            ctx.recommended_services
          ),
        }));
      })
      .catch(() => {}); // fail silently — enrichment is advisory
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id]);

  const patch = async (fields) => {
    try { const { data: d } = await api.patch(`/case-analysis/${data.id}`, fields); setData((prev) => ({ ...prev, exists: true, ...d })); }
    catch { toast.error("Save failed"); }
  };

  // Persist section review status without clobbering unsaved local edits.
  const saveSectionStatus = (nextMap) => {
    setData((d) => ({ ...d, section_reviews: nextMap }));
    api.patch(`/case-analysis/${data.id}`, { section_reviews: nextMap }).catch(() => toast.error("Couldn't save review status"));
  };

  const openSection = (key) => {
    setActiveKey(key);
    setNavOpen(false);
    try { localStorage.setItem(`ca-active-${clientId}`, key); } catch { /* noop */ }
    const cur = (data.section_reviews || {})[key];
    if (!cur || cur === "not_reviewed") saveSectionStatus({ ...(data.section_reviews || {}), [key]: "in_review" });
  };

  const approve = async () => {
    setApproving(true);
    try {
      const { data: r } = await api.post(`/case-analysis/${data.id}/approve`);
      if (r.no_estimate) {
        toast.success("Meeting review completed — no estimate required.");
        navigate(`/client-workspace/${clientId}`);
      } else {
        toast.success(`Estimate ${r.estimate_number} approved internally — open to review and send to client`);
        navigate(`/sales/estimates/${r.estimate_id}`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
    finally { setApproving(false); }
  };

  const setEstimateDisposition = async (reason_code, note) => {
    try {
      const { data: d } = await api.post(`/case-analysis/${data.id}/estimate-disposition`, { reason_code, note });
      setData((prev) => ({ ...prev, exists: true, ...d }));
      toast.success("Disposition set — Estimate section marked reviewed.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not set disposition"); }
  };

  const clearEstimateDisposition = async () => {
    try {
      const { data: d } = await api.delete(`/case-analysis/${data.id}/estimate-disposition`);
      setData((prev) => ({ ...prev, exists: true, ...d }));
      toast.success("Disposition cleared — Estimate section reset.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not clear disposition"); }
  };

  const reject = async () => {    const reason = window.prompt("Reject this AI analysis? Optionally add a reason for the record:", "");
    if (reason === null) return; // cancelled
    setRejecting(true);
    try {
      await api.post(`/case-analysis/${data.id}/reject`, { reason: reason.trim() });
      toast.success("AI analysis rejected — workflow returned to manual review.");
      navigate(`/client-workspace/${clientId}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
    finally { setRejecting(false); }
  };

  const reanalyze = async (promptMsg) => {
    // Guard: only a string may set the prompt title (prevents a click Event leaking through as onClick={reanalyze}).
    const msg = typeof promptMsg === "string" ? promptMsg : "Ask Allison to re-analyze the same transcript. Add correction notes to guide her (optional):";
    const notes = window.prompt(msg, "");
    if (notes === null) return; // cancelled
    try {
      const { data: d } = await api.post(`/case-analysis/${data.id}/reanalyze`, { notes: notes.trim() });
      toast.success("Allison is re-running the analysis…");
      initRef.current = false;
      setData({ exists: true, status: d.status, id: d.id });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not re-analyze"); }
  };
  const rerunReview = () => reanalyze("Re-run Allison's review of this case. Paste any new reference material, updated facts, or instructions for her to factor in (optional):");

  const handleArchive = async (reason, note, alsoMeeting) => {
    setArchiveBusy(true);
    try {
      await api.post(`/case-analysis/${data.id}/archive`, {
        reason, note: note || null, also_archive_meeting: alsoMeeting || false,
      });
      toast.success(alsoMeeting ? "Estimate Review and source meeting archived." : "Estimate Review archived.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Archive failed"); }
    finally { setArchiveBusy(false); }
  };

  const handleRestore = async () => {
    setRestoring(true);
    try {
      await api.post(`/case-analysis/${data.id}/restore`);
      toast.success("Estimate Review restored.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
    finally { setRestoring(false); }
  };

  const acceptUpdate = async (u) => {
    let value = u.proposed_value;
    if (typeof value !== "boolean") {
      const edited = window.prompt(`Accept "${u.label}". Edit the value if needed, then confirm:`, String(value));
      if (edited === null) return;
      value = edited.trim();
    }
    try {
      await api.post(`/case-analysis/${data.id}/record-updates/${u.id}/accept`, { value });
      toast.success(`${u.label} updated`);
      setData((d) => ({ ...d, record_updates: { ...d.record_updates, proposed: d.record_updates.proposed.map((p) => p.id === u.id ? { ...p, status: "accepted", applied_value: value } : p) } }));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not accept"); }
  };
  const rejectUpdate = async (u) => {
    try {
      await api.post(`/case-analysis/${data.id}/record-updates/${u.id}/reject`);
      toast.success(`${u.label} dismissed`);
      setData((d) => ({ ...d, record_updates: { ...d.record_updates, proposed: d.record_updates.proposed.map((p) => p.id === u.id ? { ...p, status: "rejected" } : p) } }));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reject"); }
  };

  const updateService = (i, key, val) => {
    const svcs = [...data.recommended_services];
    svcs[i] = { ...svcs[i], [key]: key === "price" ? Number(val) : val };
    servicesDirtyRef.current = true;
    setData({ ...data, recommended_services: svcs });
  };
  const removeService = (i) => {
    const next = data.recommended_services.filter((_, idx) => idx !== i);
    servicesDirtyRef.current = false;
    setData({ ...data, recommended_services: next });
    patch({ recommended_services: next });
  };
  const addService = () => { servicesDirtyRef.current = true; setData({ ...data, recommended_services: [...(data.recommended_services || []), { name: "", price: 0 }] }); };
  const addOptional = async (i) => {
    try { const { data: d } = await api.post(`/case-analysis/${data.id}/optional/add`, { index: i }); setData((prev) => ({ ...prev, ...d })); toast.success("Added to estimate"); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't add service"); }
  };
  // Dirty-state save: only persist services on blur when something actually changed.
  const saveServices = () => {
    if (!servicesDirtyRef.current) return;
    servicesDirtyRef.current = false;
    patch({ recommended_services: data.recommended_services });
  };

  /**
   * Resolve a pricing conflict for a recommended service.
   * Records the staff-confirmed amount and the resolution provenance.
   * Immediately persists to MongoDB.
   */
  const resolveConflictPrice = (serviceIdx, amount, isManual = false) => {
    const svcs = (data.recommended_services || []).map((s, i) => {
      if (i !== serviceIdx) return s;
      return {
        ...s,
        price: Number(amount),
        pricing_evidence: {
          ...s.pricing_evidence,
          staff_confirmed: true,
          staff_confirmed_amount: Number(amount),
          staff_resolution: isManual ? "manual_entry" : "selected_evidence",
        },
      };
    });
    servicesDirtyRef.current = false;
    setData(d => ({ ...d, recommended_services: svcs }));
    api.patch(`/case-analysis/${data.id}`, { recommended_services: svcs })
      .catch(() => toast.error("Could not save pricing confirmation"));
  };
  const setField = (field, val) => setData((d) => ({ ...d, [field]: val }));
  const saveField = (field) => patch({ [field]: data[field] });
  const saveSummary = (val) => { setData((d) => ({ ...d, internal_summary: val })); return patch({ internal_summary: val }); };

  if (loading) return <div className="p-10 text-slate-400 flex items-center gap-2"><Icons.Loader2 className="h-5 w-5 animate-spin" />Loading…</div>;

  // ---------- upload / empty / failed state ----------
  if (!data?.exists || data?.status === "failed") {
    return (
      <CaseIntake
        clientId={clientId}
        failed={data?.status === "failed"}
        errorMsg={data?.error}
        onBack={() => navigate(`/client-workspace/${clientId}`)}
        onStarted={setData}
      />
    );
  }

  // ---------- processing ----------
  if (data.status === "processing") return <CaseProcessing />;

  // ================= AI Review Workspace =================
  const lowConfidence = (data.confidence || 0) < 85;
  const isRejected = data.status === "rejected";
  const isArchived = data.operational_status === "archived";
  const ARCHIVE_REASON_LABELS = {
    not_client_related: "Not client related",
    incorrect_meeting_association: "Incorrect meeting association",
    duplicate: "Duplicate",
    test_internal_meeting: "Test/internal meeting",
    no_actionable_tax_service: "No actionable tax service",
    other: "Other",
  };
  const keys = sectionKeys(data);
  const activeK = keys.includes(activeKey) ? activeKey : keys[0];
  const activeIdx = keys.indexOf(activeK);
  const ru = data.record_updates || { auto_applied: [], proposed: [] };
  const autoUpdates = ru.auto_applied || [];
  const proposedUpdates = ru.proposed || [];
  const pendingUpdates = proposedUpdates.filter((p) => p.status === "proposed");
  const acceptedUpdates = proposedUpdates.filter((p) => p.status === "accepted");
  const riskCount = (data.risks || []).length;
  const missingCount = (data.missing_documents || []).length;
  const serviceCount = (data.recommended_services || []).length;
  const total = (data.recommended_services || []).reduce((t, s) => t + (Number(s.price) || 0), 0);
  const unresolvedPricingConflicts = (data.recommended_services || []).filter(
    s => s.pricing_evidence?.has_conflict && !s.pricing_evidence?.staff_confirmed
  ).length;

  const statusOf = (key) => {
    const persisted = (data.section_reviews || {})[key] || "not_reviewed";
    if (key === "record_updates" && pendingUpdates.length > 0 && persisted !== "not_reviewed") return "needs_attention";
    return persisted;
  };

  const reviewableKeys = keys.filter((k) => k !== "approval");
  const reviewableCount = reviewableKeys.length;
  const reviewedCount = reviewableKeys.filter((k) => statusOf(k) === "reviewed").length;
  const attentionKeys = reviewableKeys.filter((k) => statusOf(k) === "needs_attention");
  const firstUnfinished = reviewableKeys.find((k) => statusOf(k) !== "reviewed");
  const requiredKeys = keys.filter((k) => SECTION_META[k].group === "Case Review");
  const incompleteRequired = requiredKeys.filter((k) => statusOf(k) !== "reviewed");

  const requestApprove = () => {
    // Hard commercial guardrail: pricing conflicts must be resolved before estimate creation.
    // Skipped when no-estimate disposition is active — no estimate will be created.
    if (unresolvedPricingConflicts > 0 && !(data.estimate_disposition?.status === "not_required")) {
      toast.error("Resolve all pricing conflicts in the Services section before approving.");
      openSection("services");
      return;
    }
    if (incompleteRequired.length === 0) approve();
    else setConfirmApprove(true);
  };

  const markReviewedContinue = () => {
    const key = activeK;
    const nextKey = activeIdx < keys.length - 1 ? keys[activeIdx + 1] : null;
    const map = { ...(data.section_reviews || {}), [key]: "reviewed" };
    if (nextKey) {
      const nc = map[nextKey];
      if (!nc || nc === "not_reviewed") map[nextKey] = "in_review";
      setActiveKey(nextKey);
      try { localStorage.setItem(`ca-active-${clientId}`, nextKey); } catch { /* noop */ }
    }
    saveSectionStatus(map);
  };
  const toggleAttention = (key) => {
    const persisted = (data.section_reviews || {})[key];
    const next = persisted === "needs_attention" ? "in_review" : "needs_attention";
    saveSectionStatus({ ...(data.section_reviews || {}), [key]: next });
  };
  const goPrev = () => { if (activeIdx > 0) openSection(keys[activeIdx - 1]); };
  const goNext = () => { if (activeIdx < keys.length - 1) openSection(keys[activeIdx + 1]); };

  const rec = buildRecommendation({
    isRejected, lowConfidence, attentionCount: attentionKeys.length, pendingCount: pendingUpdates.length,
    riskCount, missingCount, confidence: data.confidence, firstUnfinished,
  });

  const activeMeta = SECTION_META[activeK];
  const activeStatus = statusOf(activeK);
  const isFlagged = (data.section_reviews || {})[activeK] === "needs_attention";

  // ----- cohesive view models passed to review-workspace components -----
  const checklistReview = { keys, activeK, statusOf, reviewedCount, reviewableCount, pendingCount: pendingUpdates.length };

  const commandModel = {
    recommendation: rec.recommendation, recToneCls: rec.recToneCls, recIcon: rec.recIcon, whyBullets: rec.whyBullets, nextAction: rec.nextAction,
    confidence: data.confidence, lowConfidence, complexity: data.complexity,
    reviewedCount, reviewableCount, riskCount, missingCount, pendingCount: pendingUpdates.length, serviceCount,
    isRejected, approving, rejecting,
  };
  const commandActions = {
    onNextAction: isArchived ? () => {} : () => { if (firstUnfinished && !isRejected) openSection(firstUnfinished); else if (isRejected) rerunReview(); else openSection("approval"); },
    onApprove: isArchived ? () => {} : requestApprove,
    onRerun:   isArchived ? () => {} : rerunReview,
    onReject:  isArchived ? () => {} : reject,
  };

  const containerModel = {
    activeMeta, activeIdx, keysLength: keys.length, activeStatus, activeK, isFlagged,
    markReviewedLabel: activeStatus === "reviewed" ? "Reviewed · Continue" : "Mark Reviewed & Continue",
  };
  const containerActions = { onPrev: goPrev, onNext: goNext, onToggleAttention: () => toggleAttention(activeK), onMarkReviewed: markReviewedContinue };

  const bodyModel = {
    total, reviewedCount, reviewableCount, lowConfidence, isRejected, firstUnfinished,
    pendingUpdates, attentionKeys, reviewableKeys, autoUpdates, proposedUpdates, acceptedUpdates,
    approving, rejecting, statusOf, confidence: data.confidence, unresolvedPricingConflicts,
  };
  const bodyActions = {
    patch, updateService, removeService, addService, addOptional, saveServices, setField, saveField,
    acceptUpdate, rejectUpdate, openSection, requestApprove, reject, reanalyze, saveSummary,
    resolveConflictPrice, setEstimateDisposition, clearEstimateDisposition,
  };

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto" data-testid="ca-review">
      <button onClick={() => navigate(`/client-workspace/${clientId}`)} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-4"><Icons.ArrowLeft className="h-4 w-4" />Back to workspace</button>

      {/* Archived banner — takes priority; disables all approval/review actions */}
      {isArchived && (
        <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start justify-between gap-4 flex-wrap" data-testid="ca-archived-banner">
          <div className="flex items-start gap-3 min-w-0">
            <Icons.Archive className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-amber-900" data-testid="ca-archived-title">Archived — Not Relevant</div>
              {data.archive_reason && (
                <div className="text-sm text-amber-800 mt-0.5" data-testid="ca-archived-reason">
                  Reason: {ARCHIVE_REASON_LABELS[data.archive_reason] || data.archive_reason}
                  {data.archive_note ? ` — ${data.archive_note}` : ""}
                </div>
              )}
              {data.archived_by_name && (
                <div className="text-xs text-amber-700/80 mt-1" data-testid="ca-archived-meta">
                  Archived by {data.archived_by_name}{data.archived_at ? ` on ${new Date(data.archived_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : ""}
                </div>
              )}
              <div className="text-xs text-amber-600/80 mt-1">Approval and review actions are disabled while this review is archived.</div>
            </div>
          </div>
          <button
            onClick={handleRestore} disabled={restoring}
            data-testid="ca-restore-btn"
            className="inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-white hover:bg-amber-50 text-amber-800 px-4 py-2 text-sm font-semibold transition-colors shrink-0 disabled:opacity-50"
          >
            {restoring ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.ArchiveRestore className="h-4 w-4" />}
            Restore Review
          </button>
        </div>
      )}

      {!isArchived && isRejected && (
        <div className="mb-5 rounded-xl border border-rose-200 bg-rose-50 p-4 flex items-start justify-between gap-4 flex-wrap" data-testid="ca-rejected-banner">
          <div className="flex items-start gap-3 min-w-0">
            <Icons.XCircle className="h-5 w-5 text-rose-500 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-rose-800">This AI analysis was rejected — manual review required.</div>
              {data.rejection_reason && <div className="text-sm text-rose-700 mt-0.5">Reason: {data.rejection_reason}</div>}
              <div className="text-xs text-rose-600/80 mt-1">The workflow did not advance. Ask Allison to re-analyze the same transcript with your corrections, or choose a next action manually from the workspace.</div>
            </div>
          </div>
          <button onClick={() => reanalyze()} data-testid="ca-reanalyze" className="inline-flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-semibold shadow-sm transition-colors shrink-0"><Icons.Sparkles className="h-4 w-4" />Ask Allison to Re-analyze</button>
        </div>
      )}

      <ReviewOverview
        data={data}
        rec={rec}
        counts={{ reviewedCount, reviewableCount, riskCount, missingCount, pendingCount: pendingUpdates.length, irsDetected: !!data.irs_signals?.detected }}
        confidence={data.confidence}
        complexity={data.complexity}
        clientName={(data.client_info?.full_name || "").trim() || "Client"}
        onOpenClient={() => navigate(`/client-workspace/${clientId}`)}
        onContinue={commandActions.onNextAction}
        analysisId={data.id}
        transcriptId={data.transcript_id}
        isArchived={isArchived}
        onArchive={handleArchive}
        archiveBusy={archiveBusy}
        detail={(
          <div className="space-y-4">
            {data.irs_signals?.detected && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 flex items-start gap-2.5" data-testid="ca-irs-signal-detail">
                <Icons.ShieldAlert className="h-5 w-5 mt-0.5 shrink-0 text-rose-600" />
                <div className="text-sm">
                  <div className="font-semibold text-rose-800 flex items-center gap-2">Allison flag: possible IRS representation needed
                    <span className="text-[11px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-rose-100 border border-rose-200">{data.irs_signals.confidence} confidence</span>
                  </div>
                  <div className="mt-1 text-rose-700">Detected signals: {(data.irs_signals.signals || []).join(", ")}.</div>
                  <div className="mt-1 text-rose-600 text-[13px]">Governed IRS Representation services have been added under <b>Potential Additional Services</b> as a <b>Staff Decision</b> — review and add them to the estimate if appropriate. Nothing is auto-billed.</div>
                </div>
              </div>
            )}
            <KnowledgeRetrievalPanel research={data.research_package} summary={data.knowledge_retrieval_summary} />
            <HistoricalContextPanel context={data.case_context_package} />
          </div>
        )}
      />

      <EstimateGovernance rec={data.estimate_recommendation} />


      <div className="flex items-center gap-2 mb-3 xl:hidden">
        <button onClick={() => setNavOpen(true)} data-testid="ca-open-nav" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700"><Icons.ListChecks className="h-4 w-4" />Checklist · {reviewedCount}/{reviewableCount}</button>
        <button onClick={() => setCardOpen(true)} data-testid="ca-open-card" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700"><Icons.Sparkles className="h-4 w-4 text-amber-500" />AI Summary</button>
      </div>

      <div className="flex gap-5 items-start">
        <aside className="hidden xl:block w-64 shrink-0 sticky top-4"><ReviewChecklist review={checklistReview} onOpen={openSection} /></aside>
        <main className="flex-1 min-w-0 pb-20 xl:pb-0">
          <ReviewSectionContainer model={containerModel} actions={containerActions}>
            <ReviewSectionBody data={data} activeK={activeK} model={bodyModel} actions={bodyActions} currencyCtx={currencyCtx} />
          </ReviewSectionContainer>
        </main>
        <aside className="hidden xl:block w-80 shrink-0 sticky top-4"><ReviewCommandCard model={commandModel} actions={commandActions} /></aside>
      </div>

      {/* mobile left drawer */}
      {navOpen && (
        <div className="fixed inset-0 z-50 xl:hidden" data-testid="ca-nav-drawer">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setNavOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-white shadow-xl p-4 overflow-y-auto">
            <div className="flex justify-end mb-2"><button onClick={() => setNavOpen(false)}><Icons.X className="h-5 w-5 text-slate-400" /></button></div>
            <ReviewChecklist review={checklistReview} onOpen={openSection} />
          </div>
        </div>
      )}
      {/* mobile right drawer */}
      {cardOpen && (
        <div className="fixed inset-0 z-50 xl:hidden" data-testid="ca-card-drawer">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setCardOpen(false)} />
          <div className="absolute inset-y-0 right-0 w-80 max-w-[90vw] bg-slate-50 shadow-xl p-4 overflow-y-auto">
            <div className="flex justify-end mb-2"><button onClick={() => setCardOpen(false)}><Icons.X className="h-5 w-5 text-slate-400" /></button></div>
            <ReviewCommandCard model={commandModel} actions={commandActions} />
          </div>
        </div>
      )}

      {/* Approval guardrail — soft, non-blocking confirmation */}
      {confirmApprove && (
        <ApprovalGuardrailDialog
          incompleteRequired={incompleteRequired}
          statusOf={statusOf}
          onClose={() => setConfirmApprove(false)}
          onReturn={() => { setConfirmApprove(false); openSection(incompleteRequired[0]); }}
          onApprove={() => { setConfirmApprove(false); approve(); }}
        />
      )}

      {/* mobile sticky action bar */}
      <div className="fixed bottom-0 left-0 right-0 z-30 xl:hidden bg-white border-t border-slate-200 px-4 py-2.5 flex items-center gap-2" data-testid="ca-mobile-actions">
        {isArchived ? (
          <button onClick={handleRestore} disabled={restoring} data-testid="ca-mobile-restore-btn"
            className="flex-1 inline-flex items-center justify-center gap-2 border border-amber-300 text-amber-800 font-semibold rounded-lg px-4 py-2.5 text-sm disabled:opacity-50">
            {restoring ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.ArchiveRestore className="h-4 w-4" />}Restore Review
          </button>
        ) : isRejected ? (
          <button onClick={() => reanalyze()} className="flex-1 inline-flex items-center justify-center gap-2 bg-blue-600 text-white font-semibold rounded-lg px-4 py-2.5 text-sm"><Icons.Sparkles className="h-4 w-4" />Re-analyze</button>
        ) : (
          <>
            <button onClick={reject} disabled={rejecting || approving} className="inline-flex items-center justify-center gap-1.5 border border-rose-200 text-rose-700 font-semibold rounded-lg px-4 py-2.5 text-sm disabled:opacity-50"><Icons.XCircle className="h-4 w-4" />Reject</button>
            <button onClick={requestApprove} disabled={approving || rejecting || (unresolvedPricingConflicts > 0 && !(data.estimate_disposition?.status === "not_required"))} className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-600 text-white font-semibold rounded-lg px-4 py-2.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed">{approving ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.CheckCircle2 className="h-4 w-4" />}{data.estimate_disposition?.status === "not_required" ? "Complete Review — No Estimate" : "Approve Estimate"}</button>
          </>
        )}
      </div>
    </div>
  );
}
